/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FunctionComponent, JSX, ReactNode } from "react";

import { Locale, LocaleKeys } from "../../../viewModel/locale";
import { Tile } from "../Tile";
import SeeMoreTile from "./SeeMoreTile";
import { getTileDimensions } from "../../../viewModel/utility/CarouselTemplateUtil";
import CarouselArrow from "./CarouselArrow";
import type { CarouselProps } from "./CarouselProps";
import "./Carousel.scss";
import { Caret, CaretDirection } from "../Caret";
import { useRailEntryAnnouncement } from "./useRailEntryAnnouncement";

/** Gap between tiles in vw; must stay in sync with Carousel.scss $carousel-tile-gap. */
const TILE_GAP_VW: number = 1.563;

/**
 * Width (vw) of the "glimpse" — partial previous tile shown on the left when scrolled.
 */
const GLIMPSE_WIDTH_VW: number = 1.837;

/**
 * Padding (vw) the viewport carries on each side so focus rings and hover scale
 * can bleed outside the rail; must stay in sync with the `padding`/`margin` pair
 * on `.carousel__viewport` in Carousel.scss. `clientWidth` includes it, so it has
 * to come off again before the width is measured against tile widths.
 */
const VIEWPORT_BLEED_VW: number = 1;

/**
 * Number of tiles to render on each side of the focused tile.
 * Tiles outside this window are not rendered (lazy loading).
 * Buffer of 7 ensures smooth navigation without visible pop-in.
 */
const RENDER_WINDOW_BUFFER: number = 7;

const CarouselComponent: FunctionComponent<CarouselProps> = (
  props: CarouselProps
): JSX.Element => {
  const {
    title,
    isChannelThumbnail,
    showChannelLogo,
    items,
    template,
    focusedIndex,
    isActive,
    isHighlightFixed,
    onTileFocus,
    onTileHover,
    onTileClick,
    renderTilePreview,
    showBrowserArrows,
    showTvArrows,
    enableGhostHover,
    glimpseCompensationVw,
    showSeeMoreTile,
    seeMoreLabel,
    onSeeMoreClick,
    syncAccessibilityFocus = false,
    getTileAccessibilityLabel
  } = props;

  // VoiceView: the rail title is spoken with the first tile focused after
  // entering the rail, then dropped for moves within it.
  const railEntryPrefix: string | undefined = useRailEntryAnnouncement(
    isActive,
    focusedIndex,
    title,
    syncAccessibilityFocus
  );

  // Browser-only: track which tile the mouse is hovering over.
  // null means no tile is hovered — no highlight should be visible.
  const [hoveredTileIndex, setHoveredTileIndex] = useState<number | null>(null);

  // Browser-only: scroll position decoupled from focus/highlight.
  // Only updated via arrow buttons, NOT on hover.
  const [browserScrollIndex, setBrowserScrollIndex] = useState<number>(0);

  // Ref for the viewport element; used by ResizeObserver to measure visible tile capacity.
  const viewportRef = useRef<HTMLDivElement>(null);

  // How many tiles fit fully inside the visible viewport width.
  // Recalculated on mount and whenever viewport size changes.
  const [visibleTileCount, setVisibleTileCount] = useState<number>(0);

  const tileWidth: number = getTileDimensions(template).widthVw;

  /**
   * Focusable slots on the rail: one per item, plus the See More tile when the
   * rail has one. Every scroll and focus bound below counts slots rather than
   * items, so the trailing tile is reachable and the rail can scroll far enough
   * to show it.
   */
  const hasSeeMoreTile: boolean = showSeeMoreTile === true;
  const slotCount: number = items.length + (hasSeeMoreTile ? 1 : 0);
  const seeMoreIndex: number = items.length;

  /**
   * Moving highlight: the rail only scrolls once focus steps past the tiles
   * on screen (the focus ring itself lives on the tile — see Tile.scss — and
   * always travels with it regardless of this mode). Opt-in via
   * `isHighlightFixed={false}`; the pointer path keeps its own hover highlight
   * and is unaffected.
   */
  const isMovingHighlight: boolean =
    isHighlightFixed === false && showBrowserArrows !== true;

  /**
   * Leftmost slot the rail is scrolled to, ignoring the moving-highlight paging
   * that is derived from the measured capacity further down. Kept separate so
   * the glimpse offset below can be known before the measurement runs.
   */
  const railScrollIndex: number =
    showBrowserArrows === true ? browserScrollIndex : focusedIndex;

  // When there are tiles to the left (railScrollIndex > 0), reduce scroll amount
  // to keep a partial glimpse of the previous tile visible on the left edge.
  // The glimpse belongs to the fixed highlight: it hints at the tiles that have
  // slid under the pinned ring. With a moving highlight the previous tiles are
  // simply on screen, and the inset would push the last tile past the viewport
  // edge — where the travelling ring would be clipped — so it stays off.
  const hasLeftContent: boolean = railScrollIndex > 0 && !isMovingHighlight;
  const glimpseCompensation: number =
    glimpseCompensationVw === undefined
      ? GLIMPSE_WIDTH_VW + TILE_GAP_VW
      : glimpseCompensationVw;
  const glimpseOffset: number = hasLeftContent ? glimpseCompensation : 0;

  // Recalculate visible tile capacity on mount, resize, and template change.
  useEffect((): (() => void) => {
    const calculateVisibleTiles = (): void => {
      const viewportEl = viewportRef.current;
      if (viewportEl === null || viewportEl === undefined) {
        return;
      }
      // Viewport width available for tiles, in vw
      const viewportWidthPx: number = viewportEl.clientWidth;
      const totalVwPx: number = document.documentElement.clientWidth;
      if (totalVwPx <= 0) {
        return;
      }
      const viewportVw: number = (viewportWidthPx / totalVwPx) * 100;
      // Tiles are laid out in the content box, so the bleed padding on either
      // side is not room a tile can occupy. Counting it in overstated the rail by
      // 2vw and could claim one tile more than actually fits.
      //
      // The glimpse state drops the container's left padding and gives it back as
      // a track translation, so the measured content box grows while the space a
      // tile can actually occupy does not. Taking the offset off again keeps the
      // capacity identical scrolled and unscrolled — otherwise the rail claims an
      // extra tile once scrolled and clips the trailing one.
      const railVw: number = Math.max(
        viewportVw - VIEWPORT_BLEED_VW * 2 - glimpseOffset,
        0
      );
      const tileStepVw: number = tileWidth + TILE_GAP_VW;
      // Number of full tiles that fit — first tile has no preceding gap
      const count: number = Math.floor((railVw + TILE_GAP_VW) / tileStepVw);
      setVisibleTileCount(Math.max(count, 1));
    };

    calculateVisibleTiles();

    const viewportEl = viewportRef.current;
    if (viewportEl === null || viewportEl === undefined) {
      return (): void => {
        /* no-op */
      };
    }

    const observer = new ResizeObserver((): void => {
      calculateVisibleTiles();
    });
    observer.observe(viewportEl);

    return (): void => {
      observer.disconnect();
    };
  }, [tileWidth, glimpseOffset]);

  /**
   * Moving highlight only: index of the leftmost visible tile.
   *
   * The rail is paged — the focused index picks the page that holds it, so the
   * rail sits still while focus crosses that page and advances by a whole page
   * once focus steps past its edge. Derived rather than remembered, so the rail
   * position is a pure function of the focused index: the same index always puts
   * the row in the same place, whichever way focus arrived and however many times
   * the row re-renders.
   */
  const pageStartIndex: number = useMemo((): number => {
    if (!isMovingHighlight || visibleTileCount <= 0) {
      return 0;
    }
    const maxStart: number = Math.max(0, slotCount - visibleTileCount);
    const pageStart: number =
      Math.floor(Math.max(focusedIndex, 0) / visibleTileCount) *
      visibleTileCount;
    return Math.min(pageStart, maxStart);
  }, [isMovingHighlight, focusedIndex, visibleTileCount, slotCount]);

  // In browser mode scroll is driven by local browserScrollIndex; in SmartTV
  // mode by the Redux focusedIndex (fixed highlight) or by the page start
  // (moving highlight, where focus and scroll are decoupled).
  const scrollIndex: number = isMovingHighlight
    ? pageStartIndex
    : railScrollIndex;

  const translateX: number =
    scrollIndex > 0
      ? -(scrollIndex * (tileWidth + TILE_GAP_VW)) + glimpseOffset
      : 0;

  const trackStyle: CSSProperties = {
    transform: "translateX(" + translateX + "vw)"
  };

  /** True when all tiles already fit within the viewport — no scrolling needed. */
  const allTilesVisible: boolean = slotCount <= visibleTileCount;

  /** Maximum scroll index: the last position where the rightmost tile is visible. */
  const maxScrollIndex: number = allTilesVisible
    ? 0
    : slotCount - visibleTileCount;

  /** Whether the carousel is at the leftmost position (no content to the left). */
  const isAtStart: boolean = allTilesVisible || scrollIndex <= 0;
  /** Whether the carousel is at the rightmost position (no content to the right). */
  const isAtEnd: boolean = allTilesVisible || scrollIndex >= maxScrollIndex;

  const handleScrollLeft = useCallback((): void => {
    if (showBrowserArrows === true) {
      const newIndex: number =
        browserScrollIndex > 0 ? browserScrollIndex - 1 : browserScrollIndex;
      if (newIndex !== browserScrollIndex) {
        setBrowserScrollIndex(newIndex);
        // Update focus in Redux so prefetch logic can track tile position
        onTileFocus(newIndex);
      }
    } else if (focusedIndex > 0) {
      onTileFocus(focusedIndex - 1);
    }
  }, [showBrowserArrows, focusedIndex, browserScrollIndex, onTileFocus]);

  const handleScrollRight = useCallback((): void => {
    if (showBrowserArrows === true) {
      const newIndex: number =
        browserScrollIndex < maxScrollIndex
          ? browserScrollIndex + 1
          : browserScrollIndex;
      if (newIndex !== browserScrollIndex) {
        setBrowserScrollIndex(newIndex);
        // Update focus in Redux so prefetch logic can track tile position
        onTileFocus(newIndex);
      }
    } else if (focusedIndex < slotCount - 1) {
      onTileFocus(focusedIndex + 1);
    }
  }, [
    showBrowserArrows,
    focusedIndex,
    browserScrollIndex,
    slotCount,
    maxScrollIndex,
    onTileFocus
  ]);

  // Calculate the render window: only render tiles within buffer of focused tile
  // This provides lazy loading — tiles outside the window are not rendered
  const renderWindow = useMemo((): { startIndex: number; endIndex: number } => {
    // With a fixed highlight the focused tile *is* the leftmost visible one, so
    // the scroll index alone anchors the window. With a moving highlight focus
    // and scroll are decoupled, so the window has to span both plus the tiles on
    // screen between them.
    const anchorStart: number = isMovingHighlight
      ? Math.min(scrollIndex, focusedIndex)
      : scrollIndex;
    const anchorEnd: number = isMovingHighlight
      ? Math.max(scrollIndex + visibleTileCount - 1, focusedIndex)
      : scrollIndex;
    const start = Math.max(0, anchorStart - RENDER_WINDOW_BUFFER);
    const end = Math.min(slotCount - 1, anchorEnd + RENDER_WINDOW_BUFFER);
    return { startIndex: start, endIndex: end };
  }, [
    isMovingHighlight,
    scrollIndex,
    focusedIndex,
    visibleTileCount,
    slotCount
  ]);

  // Create visible items slice with their original indices preserved.
  // The See More slot has no entry in `items`, so it falls out here and is
  // rendered separately below — keeping this loop about content only.
  const visibleItems = useMemo((): Array<{
    item: (typeof items)[0];
    index: number;
  }> => {
    const result: Array<{ item: (typeof items)[0]; index: number }> = [];
    for (let i = renderWindow.startIndex; i <= renderWindow.endIndex; i++) {
      const item = items[i];
      if (item !== undefined) {
        result.push({ item, index: i });
      }
    }
    return result;
  }, [items, renderWindow.startIndex, renderWindow.endIndex]);

  // Calculate left offset to position first visible tile correctly
  // This maintains visual continuity when tiles outside window aren't rendered
  const windowOffsetStyle: CSSProperties = useMemo((): CSSProperties => {
    const offsetVw = renderWindow.startIndex * (tileWidth + TILE_GAP_VW);
    return { marginLeft: offsetVw + "vw" };
  }, [renderWindow.startIndex, tileWidth]);

  // Dismiss ghost highlight on keyDown.
  useEffect((): (() => void) | void => {
    if (enableGhostHover !== true) {
      return;
    }
    const clearHover = (): void => {
      setHoveredTileIndex(null);
    };
    window.addEventListener("keydown", clearHover);
    return (): void => {
      window.removeEventListener("keydown", clearHover);
    };
  }, [enableGhostHover]);

  /**
   * Whether the See More tile is inside the render window. Outside it the tile
   * is not mounted at all, exactly as content tiles are not — the trailing
   * slot gets no special treatment.
   */
  const isSeeMoreRendered: boolean =
    hasSeeMoreTile && renderWindow.endIndex >= seeMoreIndex;

  const resolvedSeeMoreLabel: string =
    seeMoreLabel === undefined || seeMoreLabel === ""
      ? Locale.getString(LocaleKeys.SEE_MORE_TILE_LABEL)
      : seeMoreLabel;

  const handleSeeMoreClick = useCallback((): void => {
    onTileFocus(seeMoreIndex);
    if (onSeeMoreClick !== undefined) {
      onSeeMoreClick();
    }
  }, [onTileFocus, onSeeMoreClick, seeMoreIndex]);

  const tileAspectRatio: number = getTileDimensions(template).aspectRatio;
  const tileImageHeightVw: number = tileWidth * tileAspectRatio;

  const containerStyle: CSSProperties = {
    "--carousel-tile-image-height": tileImageHeightVw + "vw"
  } as CSSProperties;

  // VoiceView: in sync mode only the focused tile speaks — no region name or
  // heading is announced when focus enters a new rail.
  const a11yProps: {
    region: { role?: string; "aria-label"?: string };
    heading: { "aria-hidden"?: boolean };
  } = syncAccessibilityFocus
    ? { region: {}, heading: { "aria-hidden": true } }
    : { region: { role: "region", "aria-label": title }, heading: {} };

  return (
    <div
      className={
        "carousel" +
        (showTvArrows === true && isActive ? " carousel--tv-active" : "")
      }
      {...a11yProps.region}
    >
      <h3 className="carousel__title" {...a11yProps.heading}>
        {title}
      </h3>
      <div
        className={
          "carousel__container" +
          (hasLeftContent ? " carousel__container--has-glimpse" : "")
        }
        style={containerStyle}
      >
        {showTvArrows === true && !isAtStart && (
          <Caret
            direction={CaretDirection.LEFT}
            isDisabled={focusedIndex <= 0}
            onClick={handleScrollLeft}
            hideFromScreenReader={syncAccessibilityFocus}
            className="carousel-caret carousel-caret--west"
          />
        )}
        {showTvArrows === true && (
          <Caret
            direction={CaretDirection.RIGHT}
            isDisabled={focusedIndex >= slotCount - 1}
            onClick={handleScrollRight}
            hideFromScreenReader={syncAccessibilityFocus}
            className="carousel-caret carousel-caret--east"
          />
        )}
        <div className="carousel__viewport" ref={viewportRef}>
          <div className="carousel__track" style={trackStyle}>
            <div
              className="carousel__window"
              style={windowOffsetStyle}
              data-start={renderWindow.startIndex}
              data-end={renderWindow.endIndex}
            >
              {visibleItems.map(
                (entry: { item: (typeof items)[0]; index: number }) => {
                  const isTileFocused: boolean =
                    showBrowserArrows === true
                      ? hoveredTileIndex === entry.index
                      : isActive && entry.index === focusedIndex;
                  const isGhostHovered: boolean =
                    enableGhostHover === true &&
                    hoveredTileIndex === entry.index &&
                    !isTileFocused;
                  return (
                    <Tile
                      key={entry.index}
                      feedItem={entry.item}
                      template={template}
                      isFocused={isTileFocused}
                      isAccessibilityFocused={
                        syncAccessibilityFocus ? isTileFocused : undefined
                      }
                      getAccessibilityLabel={getTileAccessibilityLabel}
                      accessibilityLabelPrefix={railEntryPrefix}
                      isGhostHovered={isGhostHovered}
                      onFocus={(): void => onTileFocus(entry.index)}
                      onClick={(): void => onTileClick(entry.index)}
                      onMouseEnter={(): void => {
                        setHoveredTileIndex(entry.index);
                        if (onTileHover !== undefined) {
                          onTileHover(entry.index);
                        }
                      }}
                      onMouseLeave={(): void => setHoveredTileIndex(null)}
                      onKeyDown={(): void => setHoveredTileIndex(null)}
                      isChannelThumbnail={isChannelThumbnail}
                      showChannelLogo={showChannelLogo}
                      renderPreview={
                        renderTilePreview !== undefined
                          ? (focused: boolean): ReactNode =>
                              renderTilePreview(entry.item, focused)
                          : undefined
                      }
                    />
                  );
                }
              )}
              {isSeeMoreRendered && (
                <SeeMoreTile
                  label={resolvedSeeMoreLabel}
                  template={template}
                  isFocused={
                    showBrowserArrows === true
                      ? hoveredTileIndex === seeMoreIndex
                      : isActive && focusedIndex === seeMoreIndex
                  }
                  isGhostHovered={
                    enableGhostHover === true &&
                    hoveredTileIndex === seeMoreIndex &&
                    focusedIndex !== seeMoreIndex
                  }
                  isAccessibilityFocused={
                    syncAccessibilityFocus
                      ? showBrowserArrows !== true &&
                        isActive &&
                        focusedIndex === seeMoreIndex
                      : undefined
                  }
                  accessibilityLabelPrefix={railEntryPrefix}
                  onFocus={(): void => onTileFocus(seeMoreIndex)}
                  onClick={handleSeeMoreClick}
                  onMouseEnter={(): void => {
                    setHoveredTileIndex(seeMoreIndex);
                  }}
                  onMouseLeave={(): void => setHoveredTileIndex(null)}
                  onKeyDown={(): void => setHoveredTileIndex(null)}
                />
              )}
            </div>
          </div>
        </div>
        {showBrowserArrows === true && !isAtStart && (
          <CarouselArrow
            direction="left"
            isDisabled={false}
            onClick={handleScrollLeft}
          />
        )}
        {showBrowserArrows === true && !isAtEnd && (
          <CarouselArrow
            direction="right"
            isDisabled={false}
            onClick={handleScrollRight}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Memoized Carousel component to prevent unnecessary re-renders.
 * Uses default shallow comparison on all props to ensure callback
 * updates (onTileFocus, onTileClick) are never missed.
 */
const Carousel = memo(CarouselComponent);

export default Carousel;
